import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import { cloneDeep, get, keyBy } from 'lodash';
import { getThemeParameters } from '../../config/theme/selector';
import flattenKeyValuePairArrayToObject from '../../Shared/utils/flattenKeyValuePairArrayToObject';
import { getProfilePageParameters } from '../state/selectors';
import { getShowAllReviews } from '../components/Reviews/selector';
import { REVIEWS_TO_SHOW_ON_DESKTOP, REVIEWS_TO_SHOW_ON_DESKTOP_1Z } from '../components/Reviews/constants';
import isZocdocBrand from '../../utils/isZocdocBrand';

export const GET_PROVIDER = gql`
    query getProvider(
        $providerId: String
        $directoryId: String
        $specialtyId: String
        $maxReviews: Int
    ) {
        provider(id: $providerId, directoryId: $directoryId) {
            errorCode
            id
            monolithId
            status
            profileUrl
            isApproved
            prenominal
            postnominal
            firstName
            lastName
            directoryIds
            approvedFullName
            nameInSentence
            sex
            circlePictureUrl
            squarePictureUrl
            showPhotoWithCircleCss
            smallPictures
            averageRating
            isPreview
            personIdentifierSource
            dataProvidedBy
            dataProvidedByUrl
            optOutOfRatings
            practices {
                id
                name
                url
                isFeePerBooking
                isUrgentCare
            }
            relevantSpecialty(searchSpecialtyId: $specialtyId) {
                id
                name
            }
            specialties {
                id
                name
            }
            approvedLocations {
                id
                monolithId
                phone
                phoneExt
                fax
                name
                address1
                address2
                city
                state
                zipCode
                latitude
                longitude
                practiceName
                officeHours {
                    day
                    time
                }
                officeHoursDict {
                    monday
                    tuesday
                    wednesday
                    thursday
                    friday
                    saturday
                    sunday
                }
                properties {
                    key
                    value
                }
            }
            statement
            defaultProcedureId
            hospitalAffiliations
            certifications
            education {
                degreeType
                instituteName
                startDate
                endDate
            }
            memberships
            awards
            badges
            languages
            npi
            inNetworkInsurance {
                carrierId
                carrierName
                plans {
                    planId
                    planName
                    locations {
                        locationId
                    }
                }
            }
            procedures {
                id
                insuranceMayNotCover
                name
            }
            popularProcedures {
                id
                name
                insuranceMayNotCover
            }
            defaultProcedureId
            isBookable
            relevantBreadcrumbs(searchSpecialtyId: $specialtyId) {
                image
                url
                text
            }
            reviewCount
            reviews(limit: $maxReviews) {
                patientName
                date
                dateRange
                comment
                overallRating
                bedsideRating
                waitTimeRating
                isPartnerReview
                isMailedInReview
            }
            properties {
                key
                value
            }
        }
    }
`;

export const parseGql = ({ data }) => {
    // Because of the way apollo-client handles errors, we are including an
    // errorCode in the provider object rather than relying on data.error
    const errorCode = get(data, 'provider.errorCode');
    if (data.error || errorCode) {
        return {
            hasErrors: true,
            errorCode,
        };
    }

    const { provider } = data;

    const locations = provider.approvedLocations.map(loc => ({
        ...loc,
        properties: flattenKeyValuePairArrayToObject(loc.properties),
    }));

    const locationMap = keyBy(locations, 'id');

    const inNetworkInsurance = cloneDeep(provider.inNetworkInsurance) || [];

    inNetworkInsurance.forEach(carrier => {
        carrier.plans.forEach(plan => {
            if (get(plan, 'locations.length')) {
                plan.locations = plan.locations.map(location => locationMap[location.id]);
            }
        });
    });

    return {
        provider: {
            ...provider,
            locations,
            inNetworkInsurance,
            properties: flattenKeyValuePairArrayToObject(provider.properties),
        },
    };
};

export const gqlOptions = ({
    directoryId,
    providerId,
    specialtyId,
    showAllReviews,
}) => {
    let maxReviews = isZocdocBrand(directoryId) ? REVIEWS_TO_SHOW_ON_DESKTOP_1Z : REVIEWS_TO_SHOW_ON_DESKTOP;

    if (showAllReviews) {
        maxReviews = null;
    }

    return {
        variables: {
            providerId,
            directoryId,
            specialtyId,
            maxReviews,
        },
    };
};

const mapStateToProps = state => {
    const { directoryId } = getThemeParameters(state);
    return {
        directoryId,
        specialtyId: get(
            getProfilePageParameters(state),
            'initParameters.specialtyId',
            '-1',
        ),
        showAllReviews: getShowAllReviews(state),
    };
};

export const withProvider = component => {
    return connect(mapStateToProps, {})(
        graphql(GET_PROVIDER, {
            skip: ({ providerId }) => {
                return !providerId;
            },
            options: gqlOptions,
            props: parseGql,
        })(component)
    );
};
